import { useMutation, useQuery, useQueryClient, type UseMutationOptions, type UseQueryOptions } from "@tanstack/react-query";
import type { Endpoint, EndpointInput, EndpointOutput } from "@/contracts/endpoint";
import { call, type ApiError } from "./client";

/** Query key for an endpoint call; invalidate with `[ep.id]` to hit every variant. */
export const keyOf = <E extends Endpoint>(ep: E, input?: EndpointInput<E>) => (input ? [ep.id, input] : [ep.id]) as readonly unknown[];

export function useApiQuery<E extends Endpoint>(ep: E, input?: EndpointInput<E>, options?: Omit<UseQueryOptions<EndpointOutput<E>, ApiError>, "queryKey" | "queryFn">) {
  return useQuery<EndpointOutput<E>, ApiError>({ queryKey: keyOf(ep, input), queryFn: () => call(ep, input), ...options });
}

export function useApiMutation<E extends Endpoint, Ctx = unknown>(
  ep: E,
  options?: UseMutationOptions<EndpointOutput<E>, ApiError, EndpointInput<E>, Ctx> & {
    /** Endpoints whose cached queries become stale after success. */
    invalidates?: Endpoint[];
  },
) {
  const qc = useQueryClient();
  const { invalidates, onSuccess, ...rest } = options ?? {};
  return useMutation<EndpointOutput<E>, ApiError, EndpointInput<E>, Ctx>({
    mutationFn: (input) => call(ep, input),
    onSuccess: async (...args) => {
      // Not awaited: the refetch must not hold back the caller's per-call callbacks (see web/src/api/hooks.ts).
      void Promise.all((invalidates ?? []).map((e) => qc.invalidateQueries({ queryKey: [e.id] })));
      return onSuccess?.(...args);
    },
    ...rest,
  });
}
